import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { Layout, LoadingScreen, StatusPill } from '../components/Layout';
import { useMe } from '../components/AuthGuard';
import { api } from '../lib/api';
import { processingStatusText } from '../lib/processing-status';

type SearchScope = 'meetings' | 'documents' | 'transcripts' | 'actions';
type SearchEstimate = {
  sources: Array<{
    id: string;
    kind: string;
    title: string;
    content: string;
    heldAt: string | null;
  }>;
  sourceCount: number;
  estimatedInputTokens: number;
  estimatedOutputTokens: number;
  estimatedUsd: number;
  estimatedThb: number;
  usdThbRate: number;
  rateUpdatedAt: string;
  model: string;
};

interface DashboardItem {
  id: string;
  title: string | null;
  start_at: string | null;
  organization: string | null;
  status: string;
}

interface DashboardData {
  nextAppointment: DashboardItem | null;
  pendingReviewCount: number;
  recent: DashboardItem[];
  googleConnected: boolean;
  processing: Array<{
    id: string;
    title: string | null;
    status: string;
    processing_total_pages: number;
    processing_completed_pages: number;
    processing_stage: string | null;
  }>;
  openActions: Array<{
    id: string;
    title: string;
    details: string | null;
    primary_owner_name: string | null;
    meeting_id: string;
    topic_id: string;
    meeting_title: string;
    held_at: string | null;
  }>;
  todayItems: Array<{
    id: string;
    title: string | null;
    itemType: string;
    appointment_date?: string | null;
    start_at?: string | null;
    held_at?: string | null;
    topic_id?: string;
  }>;
}

export function DashboardPage() {
  const me = useMe();
  const previousProcessing = useRef<Set<string> | null>(null);
  const [completedNotice, setCompletedNotice] = useState(false);
  const [search, setSearch] = useState('');
  const [scopes, setScopes] = useState<SearchScope[]>([
    'meetings',
    'documents',
    'transcripts',
    'actions',
  ]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [estimate, setEstimate] = useState<SearchEstimate | null>(null);
  const [answer, setAnswer] = useState<{ text: string; actualThb: number } | null>(null);
  const [searching, setSearching] = useState(false);
  const runEstimate = async () => {
    if (!search.trim() || !scopes.length) return;
    setSearching(true);
    setAnswer(null);
    try {
      const response = await api<{ data: SearchEstimate }>('/api/meeting-search/estimate', {
        method: 'POST',
        body: JSON.stringify({
          query: search,
          scopes,
          ...(dateFrom ? { dateFrom } : {}),
          ...(dateTo ? { dateTo } : {}),
        }),
      });
      setEstimate(response.data);
    } finally {
      setSearching(false);
    }
  };
  const runAi = async () => {
    if (!estimate) return;
    setSearching(true);
    try {
      const response = await api<{ data: { answer: string; actualThb: number } }>(
        '/api/meeting-search/ask',
        {
          method: 'POST',
          body: JSON.stringify({
            query: search,
            scopes,
            ...(dateFrom ? { dateFrom } : {}),
            ...(dateTo ? { dateTo } : {}),
            selectedSourceIds: estimate.sources.map((source) => source.id),
            confirmed: true,
          }),
        },
      );
      setAnswer({ text: response.data.answer, actualThb: response.data.actualThb });
    } finally {
      setSearching(false);
    }
  };
  const week = (() => {
    const now = new Date();
    const day = now.getDay();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day);
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    return { start: iso(start), end: iso(end) };
  })();
  const dashboard = useQuery({
    queryKey: ['dashboard'],
    queryFn: () =>
      api<{ data: DashboardData }>(`/api/dashboard?start=${week.start}&end=${week.end}`).then(
        (response) => response.data,
      ),
    refetchInterval: (query) => (query.state.data?.processing.length ? 3_000 : false),
  });
  const data = dashboard.data;
  useEffect(() => {
    if (!data) return;
    const current = new Set(data.processing.map((item) => item.id));
    if (
      previousProcessing.current &&
      [...previousProcessing.current].some((id) => !current.has(id))
    ) {
      setCompletedNotice(true);
    }
    previousProcessing.current = current;
  }, [data]);
  if (dashboard.isLoading)
    return (
      <Layout title="หน้าหลัก">
        <LoadingScreen />
      </Layout>
    );
  return (
    <Layout title={`สวัสดี ${me.data?.displayName ?? ''}`}>
      <div className="space-y-5">
        <section className="rounded-3xl border border-[#426888] bg-[#142b45] p-5 shadow-xl shadow-[#3dbbff]/10 sm:p-7">
          <p className="workspace-eyebrow">เริ่มงานได้ทันที</p>
          <h2 className="mt-2 text-2xl font-bold text-[#f0f8ff]">วันนี้ต้องทำอะไรต่อ?</h2>
          <p className="mt-1 text-sm text-[#b9d1e5]">สร้างรายการใหม่ หรือนำเข้าข้อมูลที่มีอยู่</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <Link to="/create" className="btn-primary">
              ＋ สร้างรายการ
            </Link>
            <Link
              to="/calendar/import"
              className="flex min-h-12 items-center justify-center rounded-xl border border-[#527da1] px-5 py-3 font-semibold text-[#b8e6ff] transition hover:bg-[#254766]"
            >
              นำเข้าข้อมูล
            </Link>
          </div>
        </section>
        <section className="card">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">รายการสัปดาห์นี้</h2>
            <span className="text-sm text-slate-500">อา. – ส.</span>
          </div>
          <div className="mt-3 space-y-2">
            {(data?.todayItems ?? []).map((item) => (
              <Link
                key={`${item.itemType}-${item.id}`}
                to={
                  item.itemType === 'meeting'
                    ? `/meeting-topics/${item.topic_id}?meeting=${item.id}`
                    : `/appointments/${item.id}`
                }
                className="flex items-center justify-between rounded-xl border p-3"
              >
                <span>
                  <strong>{item.title ?? 'ไม่มีชื่อรายการ'}</strong>
                  <span className="ml-2 text-xs text-slate-500">
                    {item.itemType === 'meeting' ? 'การประชุม' : 'นัดหมาย'}
                  </span>
                </span>
                <span className="text-sm text-slate-500">
                  {(item.start_at ?? item.held_at ?? '').slice(0, 16).replace('T', ' ')}
                </span>
              </Link>
            ))}
            {data?.todayItems && !data.todayItems.length && (
              <p className="py-4 text-sm text-slate-500">ไม่มีรายการในสัปดาห์นี้</p>
            )}
          </div>
        </section>
        <details className="card group">
          <summary className="cursor-pointer list-none font-semibold">
            ค้นข้อมูลการประชุมด้วย AI{' '}
            <span className="float-right text-sm text-[#4b7671] group-open:rotate-180">⌄</span>
          </summary>
          <div className="mt-4 space-y-4">
            <p className="text-sm text-slate-500">กรองจากฐานข้อมูลก่อน แล้วค่อยเลือกให้ AI สรุป</p>
            <input
              className="input"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="พิมพ์คำถามหรือคำค้น…"
            />
            <div className="flex flex-wrap gap-2">
              {(
                [
                  ['meetings', 'การประชุม'],
                  ['documents', 'เอกสารแนบ'],
                  ['transcripts', 'Transcript/Summary'],
                  ['actions', 'งานติดตาม'],
                ] as const
              ).map(([value, label]) => (
                <label
                  key={value}
                  className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={scopes.includes(value)}
                    onChange={() =>
                      setScopes((current) =>
                        current.includes(value)
                          ? current.filter((item) => item !== value)
                          : [...current, value],
                      )
                    }
                  />
                  {label}
                </label>
              ))}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-sm">
                ตั้งแต่
                <input
                  type="date"
                  className="input mt-1"
                  value={dateFrom}
                  onChange={(event) => setDateFrom(event.target.value)}
                />
              </label>
              <label className="text-sm">
                ถึง
                <input
                  type="date"
                  className="input mt-1"
                  value={dateTo}
                  onChange={(event) => setDateTo(event.target.value)}
                />
              </label>
            </div>
            <button
              className="btn-primary w-full"
              onClick={() => void runEstimate()}
              disabled={searching || !search.trim() || !scopes.length}
            >
              {searching ? 'กำลังค้นฐานข้อมูล…' : 'ค้นและประเมินค่าใช้จ่าย'}
            </button>
            {estimate && (
              <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm">
                <p>
                  พบแหล่งข้อมูล {estimate.sourceCount} รายการ ·{' '}
                  {estimate.estimatedInputTokens.toLocaleString()} โทเคนเข้า /{' '}
                  {estimate.estimatedOutputTokens.toLocaleString()} โทเคนออก
                </p>
                <p>
                  ประมาณการ <strong>{estimate.estimatedThb.toFixed(4)} บาท</strong> ($
                  {estimate.estimatedUsd.toFixed(8)}) · รุ่น {estimate.model}
                </p>
                <p className="text-xs text-slate-500">
                  อัตรา USD/THB {estimate.usdThbRate.toFixed(4)} ({estimate.rateUpdatedAt})
                </p>
                <div className="flex gap-2">
                  <button className="btn-secondary flex-1" onClick={() => setEstimate(null)}>
                    ยกเลิก
                  </button>
                  <button
                    className="btn-primary flex-1"
                    onClick={() => void runAi()}
                    disabled={searching || estimate.sourceCount === 0}
                  >
                    ยืนยันให้ AI สรุป
                  </button>
                </div>
              </div>
            )}
            {answer && (
              <div className="space-y-2 rounded-xl border border-teal-200 bg-teal-50 p-4">
                <h3 className="font-semibold">ผลสรุปจาก AI</h3>
                <div className="whitespace-pre-wrap text-sm">{answer.text}</div>
                <p className="text-xs text-slate-500">
                  ค่าใช้จ่ายจริงประมาณ {answer.actualThb.toFixed(4)} บาท
                </p>
              </div>
            )}
          </div>
        </details>
        {completedNotice && (
          <div className="flex items-center justify-between gap-3 rounded-2xl bg-emerald-50 p-4 text-emerald-900">
            <span>✓ ประมวลผลเอกสารเสร็จแล้ว พร้อมให้ตรวจสอบข้อมูล</span>
            <button
              type="button"
              className="font-semibold"
              onClick={() => setCompletedNotice(false)}
            >
              ปิด
            </button>
          </div>
        )}
        {data?.processing.length ? (
          <section className="card space-y-3">
            <div>
              <h2 className="font-semibold">กำลังประมวลผลเอกสาร</h2>
              <p className="text-sm text-slate-600">งานทำต่อในเบื้องหลัง คุณเปิดหน้าอื่นได้</p>
            </div>
            {data.processing.map((item) => {
              const total = Math.max(1, item.processing_total_pages);
              const stage = processingStatusText(item.processing_stage);
              const percent = stage.isAiStage
                ? 95
                : Math.round((item.processing_completed_pages / total) * 85);
              return (
                <Link
                  key={item.id}
                  to={`/appointments/${item.id}/processing`}
                  className="block rounded-2xl border border-slate-200 p-3"
                >
                  <div className="flex justify-between gap-3 text-sm">
                    <span className="font-semibold">{item.title ?? 'เอกสารใหม่'}</span>
                    <span>
                      {item.processing_completed_pages}/{total} หน้า
                    </span>
                  </div>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-teal-600 transition-all"
                      style={{ width: `${Math.max(5, percent)}%` }}
                    />
                  </div>
                  <p className="mt-2 text-xs text-slate-500">{stage.detail}</p>
                  <p className="mt-1 text-xs font-medium text-teal-700">
                    API ที่กำลังใช้: {stage.api}
                  </p>
                </Link>
              );
            })}
          </section>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <section className="card">
            <p className="text-sm font-medium text-slate-500">นัดหมายถัดไป</p>
            {data?.nextAppointment ? (
              <Link to={`/appointments/${data.nextAppointment.id}`} className="mt-2 block">
                <h3 className="font-semibold">{data.nextAppointment.title ?? 'นัดหมาย'}</h3>
                <p className="mt-1 text-sm text-slate-600">
                  {data.nextAppointment.start_at?.replace('T', ' ')}
                </p>
                <p className="text-sm text-slate-500">{data.nextAppointment.organization}</p>
              </Link>
            ) : (
              <p className="mt-3 text-sm text-slate-500">ยังไม่มีนัดหมายในอนาคต</p>
            )}
          </section>
          <section className="card flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">รายการรอตรวจสอบ</p>
              <p className="mt-1 text-3xl font-bold text-amber-600">
                {data?.pendingReviewCount ?? 0}
              </p>
            </div>
            <Link to="/appointments" className="btn-secondary">
              ดูรายการ
            </Link>
          </section>
        </div>

        <section className="card">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">รายการล่าสุด</h2>
            <Link to="/appointments" className="text-sm font-medium text-teal-700">
              ดูทั้งหมด
            </Link>
          </div>
          <div className="mt-3 divide-y divide-slate-100">
            {data?.recent.length ? (
              data.recent.map((item) => (
                <Link
                  key={item.id}
                  to={`/appointments/${item.id}`}
                  className="flex items-center justify-between gap-3 py-3"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium">{item.title ?? 'รายการใหม่'}</p>
                    <p className="truncate text-sm text-slate-500">
                      {item.organization ?? 'ยังไม่ระบุสถานที่'}
                    </p>
                  </div>
                  <StatusPill status={item.status} />
                </Link>
              ))
            ) : (
              <p className="py-5 text-center text-sm text-slate-500">ยังไม่มีรายการ</p>
            )}
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-2">
          {(['followup', 'preparation'] as const).map((kind) => {
            const items = (data?.openActions ?? []).filter((item) =>
              kind === 'preparation'
                ? /เตรียม|agenda|เอกสาร/i.test(`${item.title} ${item.details ?? ''}`)
                : !/เตรียม|agenda|เอกสาร/i.test(`${item.title} ${item.details ?? ''}`),
            );
            return (
              <div className="card" key={kind}>
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold">
                    {kind === 'preparation' ? 'งานเตรียมประชุม' : 'งานติดตาม'}
                  </h2>
                  <span className="text-sm text-slate-500">{items.length} งานเปิด</span>
                </div>
                <div className="mt-3 space-y-2">
                  {items.slice(0, 10).map((item) => (
                    <Link
                      key={item.id}
                      to={`/meeting-topics/${item.topic_id}?meeting=${item.meeting_id}`}
                      className="block rounded-xl border border-slate-200 p-3 hover:border-teal-300"
                    >
                      <p className="font-medium">{item.title}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        {item.meeting_title}
                        {item.held_at ? ` · ${item.held_at.slice(0, 10)}` : ''}
                      </p>
                      {item.primary_owner_name && (
                        <p className="mt-1 text-xs text-slate-500">
                          ผู้รับผิดชอบ: {item.primary_owner_name}
                        </p>
                      )}
                    </Link>
                  ))}
                  {!items.length && <p className="py-4 text-sm text-slate-500">ไม่มีงานเปิด</p>}
                </div>
              </div>
            );
          })}
        </section>

        <section
          className={`card flex items-center gap-3 ${data?.googleConnected ? 'border-emerald-200' : 'border-amber-200'}`}
        >
          <span className="text-2xl">{data?.googleConnected ? '✓' : '!'}</span>
          <div>
            <p className="font-medium">Google Calendar</p>
            <p className="text-sm text-slate-500">
              {data?.googleConnected ? 'เชื่อมต่อแล้ว' : 'ต้องเชื่อมต่อใหม่'}
            </p>
          </div>
        </section>
      </div>
    </Layout>
  );
}
